import { useState } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid } from "recharts";
import { useLoad } from "@/lib/hooks";
import { fmt } from "@/lib/api";
import { Section, Field, NativeSelect } from "@/components/common";
import { PeriodFilter, today, qs } from "./common";

const iso = (d) => d.toISOString().slice(0, 10);
function range(k) {
  const n = new Date(), y = n.getUTCFullYear(), m = n.getUTCMonth();
  if (k === "last_month") return { frm: iso(new Date(Date.UTC(y, m - 1, 1))), to: iso(new Date(Date.UTC(y, m, 0))) };
  if (k === "this_year") return { frm: `${y}-01-01`, to: today() };
  return { frm: iso(new Date(Date.UTC(y, m, 1))), to: today() };
}
const PRESETS = [["this_month", "This month"], ["last_month", "Last month"], ["this_year", "This year"], ["custom", "Custom dates"]];

function Breakdown({ title, rows, total, m, testId }) {
  return (
    <Section title={title} testId={testId}><table className="po-table"><tbody>
      {rows.length === 0 && <tr><td className="text-slate-400">No revenue in this period</td></tr>}
      {rows.map((r) => <tr key={r.name || r.key}><td>{r.label || r.name}{r.parts && r.parts.length > 0 && <div className="text-xs text-slate-500">{r.parts.map((p) => `${p.name} ${m(p.amount)}`).join(" · ")}</div>}</td>
        <td className="po-num font-medium" data-testid={`${testId}-${(r.key || r.name).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>{m(r.amount)}</td></tr>)}
      <tr className="bg-[#13294B]/5 font-bold"><td>Total</td><td className="po-num" data-testid={`${testId}-total`}>{m(total)}</td></tr>
    </tbody></table></Section>
  );
}

function Tie({ d, m }) {
  return (
    <Section title="How this ties to the trial balance" testId="revenue-tie">
      <div className="space-y-2 p-5 text-sm">
        <table className="po-table"><tbody>{d.accounts.map((a) => <tr key={a.code}><td>{a.code} · {a.name}</td><td className="po-num" data-testid={`revenue-acct-${a.code}`}>{m(a.amount)}</td></tr>)}
          <tr className="font-bold"><td>Sales accounts, movement for {d.frm} to {d.to}</td><td className="po-num" data-testid="revenue-acct-total">{m(d.accounts.reduce((s, a) => s + a.amount, 0))}</td></tr></tbody></table>
        <p className="text-slate-500">The Revenue total is read from the same ledger entries as the trial balance: credits less debits on these accounts, dated in this period and in the selected branch. Credit notes post to 4050 and cancellations reverse 4000, so both reduce it. Tax is posted to the tax accounts, so it is not included.{d.other_income ? ` Other income (4900) of ${m(d.other_income)} is not sales and is left out.` : ""}</p>
      </div>
    </Section>
  );
}

export default function Revenue() {
  const [k, setK] = useState("this_month");
  const [p, setP] = useState(range("this_month"));
  const ok = p.frm && p.to && p.frm <= p.to;
  const [d] = useLoad(ok ? `/acct/revenue?${qs(p)}` : null);
  const m = (v) => fmt(v, d?.currency);
  const pick = (v) => { setK(v); if (v !== "custom") setP(range(v)); };
  return (
    <>
      <Section testId="revenue-filters"><div className="flex flex-wrap items-end gap-3 p-5">
        <Field label="Period"><NativeSelect testId="revenue-period-select" value={k} onChange={pick} options={PRESETS.map(([value, label]) => ({ value, label }))} /></Field>
        {k === "custom" && <PeriodFilter p={p} setP={setP} testId="revenue-period" />}
        {!ok && <p className="text-sm text-rose-600" data-testid="revenue-date-error">Choose a start and end date, with the start on or before the end.</p>}
        {d && ok && <div className="ml-auto text-right"><div className="text-xs uppercase tracking-wide text-slate-500">Revenue before tax · {d.frm} to {d.to}</div>
          <div className="text-3xl font-semibold text-[#13294B]" data-testid="revenue-total">{m(d.total)}</div></div>}
      </div></Section>
      {d && ok && <>
        <div className="grid gap-6 lg:grid-cols-2">
          <Breakdown title="By source" rows={d.by_source} total={d.total} m={m} testId="revenue-source" />
          <Breakdown title="By service line or product" rows={d.by_line} total={d.total} m={m} testId="revenue-line" />
        </div>
        <Section title="By month" testId="revenue-month-chart"><div className="h-72 p-4"><ResponsiveContainer width="100%" height="100%"><BarChart data={d.by_month}><CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="month" fontSize={11} /><YAxis fontSize={11} /><Tooltip formatter={(v) => m(v)} /><Legend />
          <Bar dataKey="invoiced" name="Invoiced jobs" stackId="r" fill="#13294B" /><Bar dataKey="pos" name="POS sales" stackId="r" fill="#45BFB4" />{d.by_month.some((x) => x.other) && <Bar dataKey="other" name="Other" stackId="r" fill="#F59E0B" radius={[4, 4, 0, 0]} />}</BarChart></ResponsiveContainer></div></Section>
        <Tie d={d} m={m} />
      </>}
    </>
  );
}
